// src/services/cacheManager.js
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCurrentUserId } from '../utils/authUtils';

class CacheManager {
  /**
   * Check if local cache is still valid
   * @param {string} dataType - Type of data (e.g., 'appointments', 'customers')
   * @param {number} localCacheTimestamp - Timestamp of local cache in milliseconds
   * @returns {Promise<boolean>} True if cache is valid, false if should be invalidated
   */
  async isCacheValid(dataType, localCacheTimestamp) {
    try {
      const userId = getCurrentUserId();
      const lastUpdateTime = await this.getLastUpdateTime(dataType);

      // If no remote timestamp exists, consider cache valid
      if (!lastUpdateTime) {
        console.log(`CacheManager: No remote timestamp for ${dataType}, cache is valid`);
        return true;
      }

      // If local cache is older than remote update (even by 1ms), invalidate
      const isValid = localCacheTimestamp >= lastUpdateTime;
      
      console.log(`CacheManager: Cache validation for ${dataType}`, {
        localTimestamp: new Date(localCacheTimestamp).toISOString(),
        remoteTimestamp: new Date(lastUpdateTime).toISOString(),
        diff: lastUpdateTime - localCacheTimestamp,
        isValid
      });

      return isValid;
    } catch (error) {
      console.error(`CacheManager: Error checking cache validity for ${dataType}:`, error);
      // On error, invalidate cache to be safe
      return false;
    }
  }

  /**
   * Mark that data has changed (call after mutations)
   * @param {string} dataType - Type of data that changed
   * @returns {Promise<void>}
   */
  async invalidateCache(dataType) {
    try {
      const userId = getCurrentUserId();
      const docPath = `cacheManagement/${userId}/${dataType}/lastUpdate`;
      const docRef = doc(db, ...docPath.split('/'));

      await setDoc(docRef, {
        timestamp: serverTimestamp(),
        updated_at: new Date().toISOString() // Fallback for immediate reads
      });

      console.log(`CacheManager: Invalidated cache for ${dataType}`);
    } catch (error) {
      console.error(`CacheManager: Error invalidating cache for ${dataType}:`, error);
      throw error;
    }
  }

  /**
   * Get the latest update timestamp from Firestore
   * @param {string} dataType - Type of data
   * @returns {Promise<number|null>} Timestamp in milliseconds, or null if not found
   */
  async getLastUpdateTime(dataType) {
    try {
      const userId = getCurrentUserId();
      const docPath = `cacheManagement/${userId}/${dataType}/lastUpdate`;
      const docRef = doc(db, ...docPath.split('/'));

      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // Handle Firestore Timestamp object
        if (data.timestamp?.toMillis) {
          return data.timestamp.toMillis();
        }
        
        // Fallback to ISO string
        if (data.updated_at) {
          return new Date(data.updated_at).getTime();
        }
      }

      return null;
    } catch (error) {
      console.error(`CacheManager: Error getting last update time for ${dataType}:`, error);
      return null;
    }
  }
}

export const cacheManager = new CacheManager();